import { useCallback, useRef, useMemo, type Dispatch } from "react";
import { useRouter } from "@/routing/navigation";
import {
  type AudienceSelectionRef,
  SurveyJson,
  billableUnitsForDesign,
  countStimulusCuts,
  evaluateStudyDesign,
  maxRespondentsForDesign,
  studyCreditCost,
} from "@digital-twins/shared-types";
import { usersApi } from "@/lib/api";
import { toast } from "sonner";
import i18n from "@/i18n";
import { CREATION_STORAGE_KEY, CREATION_CONSTANTS } from "../state/creationState";

const RESUME_DRAFT_STORAGE_KEY = "dtfg_resume_draft_pending";
import type { CreationState, CreationAction } from "../state/creationState";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { launchQuestionnaireDraftApi, updateQuestionnaireDraftApi } from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import {
  requestFailureLine,
  signInActionFor,
  transportFailureCopy,
} from "@/features/studies/questionnaire/utils/questionnaireIssueCopy";
import { STUDY_CREATION_TYPE, STUDY_CREATION_TYPE_ID } from "../types";
import { getEffectiveRespondentLimits } from "../utils/respondentLimits";
import { usableStudyName } from "../utils/studyName";
import { withDefaultStudyExecutionMode } from "@/lib/study-execution-mode";

export interface StudyActionHandlers {
  handleCompiledSurveyReady: (
    surveyJson: SurveyJson,
    revision: number,
    studyTitle?: string | null,
    audience?: AudienceSelectionRef | null,
  ) => Promise<boolean>;
  handleSubmit: () => Promise<void>;
  /** A rename the user typed in the "Study name" field (not yet saved). */
  editStudyName: (name: string) => void;
  /**
   * Saves the typed rename as the draft's display name (blur or Enter).
   * Resolves false when the save failed; the rename stays pending and the
   * next commit, or launch, sends it again.
   */
  commitStudyName: () => Promise<boolean>;
}

export function useStudyActions(
  state: CreationState,
  dispatch: Dispatch<CreationAction>,
): StudyActionHandlers {
  const router = useRouter();
  const submittingRef = useRef(false);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  // The draft row owns the display name; only an explicit rename writes it.
  // `pendingNameRef` is a typed name not yet saved, `nameCommitRef` the save
  // in flight. A name the field shows from the draft's title or the row is
  // never written back.
  const pendingNameRef = useRef<string | null>(null);
  const nameCommitRef = useRef<Promise<boolean> | null>(null);

  const editStudyName = useCallback(
    (name: string) => {
      pendingNameRef.current = name;
      dispatch({ type: "SET_STUDY_NAME", payload: name });
    },
    [dispatch],
  );

  const commitStudyName = useCallback(async (): Promise<boolean> => {
    const typed = pendingNameRef.current;
    if (typed === null) {
      return (await nameCommitRef.current) ?? true;
    }
    pendingNameRef.current = null;
    const displayName = usableStudyName(typed);
    const draftId = state.questionnaireDraftId;
    // An empty field is no name: launch stays disabled until one is typed.
    if (!displayName || !draftId) {
      return true;
    }
    const commit = updateQuestionnaireDraftApi(draftId, { displayName }).then(
      () => true,
      () => {
        if (pendingNameRef.current === null) {
          pendingNameRef.current = typed;
        }
        toast.error(i18n.t("creation.saveNameFailed", { ns: "app" }));
        return false;
      },
    );
    nameCommitRef.current = commit;
    return commit;
  }, [state.questionnaireDraftId]);

  const handleCompiledSurveyReady = useCallback(
    async (
      surveyJson: SurveyJson,
      revision: number,
      studyTitle?: string | null,
      audience?: AudienceSelectionRef | null,
    ): Promise<boolean> => {
      if (!state.selectedStudyType) {
        return false;
      }

      dispatch({
        type: "SET_SURVEY_JSON",
        payload: surveyJson,
        revision,
        audience: audience ?? null,
      });

      // The field shows the draft's own title until the user renames it; the
      // seed is display only and never saved as the display name (launch
      // falls back to the same title). An untitled draft leaves the field
      // empty for the user to name, never the study type's title.
      const draftTitle = usableStudyName(studyTitle);
      if (!usableStudyName(state.studyName) && draftTitle) {
        dispatch({ type: "SET_STUDY_NAME", payload: draftTitle });
      }

      dispatch({ type: "SET_STEP", payload: 3 });
      return true;
    },
    [dispatch, state.selectedStudyType, state.studyName],
  );

  const buildFinalSurveyJson = (): SurveyJson | null => {
    if (!state.editedSurveyJson || !state.selectedStudyType) return null;
    return state.editedSurveyJson;
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    dispatch({ type: "SET_LOADING", payload: true });

    const surveyJson = buildFinalSurveyJson();
    if (!surveyJson || state.reviewedRevision === null) {
      toast.error(
        i18n.t("creation.surveyDataMissing", { ns: "app" }),
      );
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }

    if (state.selectedStudyType?.id !== STUDY_CREATION_TYPE_ID) {
      toast.error(i18n.t("creation.unsupportedStudyType", { ns: "app" }), {
        description: i18n.t("creation.unsupportedStudyTypeBody", { ns: "app" }),
      });
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }

    if (!state.questionnaireDraftId) {
      dispatch({ type: "SET_STEP", payload: 2 });
      toast.error(i18n.t("creation.studySessionExpired", { ns: "app" }), {
        description: i18n.t("creation.studySessionExpiredBody", { ns: "app" }),
        duration: Infinity,
      });
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }

    const studyType = state.selectedStudyType ?? STUDY_CREATION_TYPE;
    const selectedPop = state.populations.find(
      (p) => p.id === state.selectedPopulationId,
    );
    const cutCount = countStimulusCuts(state.editedSurveyJson);
    const limits = getEffectiveRespondentLimits(studyType, {
      filteredCount: state.filteredCount,
      personaCount: selectedPop?.personaCount,
      designMax: maxRespondentsForDesign(state.design),
    });
    const maxRespondents =
      state.studyMode === "quick"
        ? Math.min(limits.max, CREATION_CONSTANTS.QUICK_MODE_MAX_RESPONDENTS)
        : limits.max;
    const respondentCount = state.numRespondents;
    if (respondentCount == null) {
      toast.error(i18n.t("creation.enterRespondents", { ns: "app" }), {
        description: i18n.t("creation.enterRespondentsBody", {
          ns: "app",
          min: limits.min,
          max: maxRespondents,
        }),
      });
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }
    if (respondentCount < limits.min || respondentCount > maxRespondents) {
      toast.error(i18n.t("creation.invalidRespondents", { ns: "app" }), {
        description: i18n.t("creation.invalidRespondentsBody", {
          ns: "app",
          min: limits.min,
          max: maxRespondents,
        }),
      });
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }

    const designDecision = evaluateStudyDesign({
      requestedDesign: state.design,
      cutCount,
      numRespondents: respondentCount,
      personaCount: selectedPop?.personaCount ?? null,
    });
    if (designDecision.refusal) {
      toast.error(i18n.t("creation.cantLaunchDesign", { ns: "app" }), {
        description: designDecision.refusal.message,
      });
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }

    try {
      const freshCredits = await usersApi.getCreditInfo();
      dispatch({ type: "SET_CREDIT_INFO", payload: freshCredits });

      const selectedPopForCost = state.populations.find(
        (p) => p.id === state.selectedPopulationId,
      );
      const costPerRespondent = selectedPopForCost?.creditsPerRespondent ?? 1;
      const totalCost = studyCreditCost({
        creditsPerRespondent: costPerRespondent,
        billableUnits: billableUnitsForDesign(
          state.design,
          respondentCount,
          cutCount,
        ),
        studyMode: state.studyMode === "quick" ? "quick" : "full",
      });

      if (freshCredits.available < totalCost) {
        const allowOverage = freshCredits.allowOverage ?? false;
        if (!allowOverage) {
          toast.error(i18n.t("creation.insufficientCredits", { ns: "app" }), {
            description: i18n.t("creation.insufficientCreditsBody", {
              ns: "app",
              needed: totalCost,
              available: freshCredits.available,
            }),
          });
          dispatch({ type: "SET_LOADING", payload: false });
          submittingRef.current = false;
          return;
        }
        const overageNeeded = totalCost - freshCredits.available;
        toast.info(i18n.t("creation.usingOverage", { ns: "app" }), {
          description: i18n.t("creation.usingOverageBody", {
            ns: "app",
            count: overageNeeded,
          }),
        });
      }
    } catch {
      // Non-blocking: if credit refresh fails, proceed with server-side check
    }

    // Launch names the experiment from the draft row, so a rename typed just
    // before Launch is saved first; a failed save does not launch.
    if (!(await commitStudyName())) {
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
      return;
    }

    try {
      const launched = await launchQuestionnaireDraftApi(
        state.questionnaireDraftId,
        // The design and the goal are the draft's StudySpec's, which the
        // launch reads (plan Stage 3, S3.4 rows 25 and 26): not sent.
        withDefaultStudyExecutionMode({
          // The revision Review compiled: the survey and price shown.
          expectedRevision: state.reviewedRevision,
          numRespondents: respondentCount,
          samplerType: "batch_probability",
          workers: CREATION_CONSTANTS.DEFAULT_WORKERS,
          idempotencyKey,
          populationId: state.selectedPopulationId || undefined,
          // The filter and saved segments are the draft's (`draft.audience`):
          // the launch reads them there, so none is sent (C09-2).
          storageId: state.selectedStorageId || undefined,
          language: state.contentLanguage || undefined,
          deckEnabled: state.deckEnabled,
          twinChatEnabled: state.twinChatEnabled,
          studyMode: state.studyMode || undefined,
        }),
      );
      // experiment_created is emitted server-side (experiment orchestration) to avoid double-counting.
      sessionStorage.removeItem(CREATION_STORAGE_KEY);
      try { localStorage.removeItem(RESUME_DRAFT_STORAGE_KEY); } catch { /* ignore */ }
      toast.success(i18n.t("creation.studyCreated", { ns: "app" }));
      router.push(`/tests/${launched.experimentId}/results`);
    } catch (error: unknown) {
      track(AnalyticsEvents.STUDY_CREATION_FAILED, {
        study_type_id: state.selectedStudyType?.id,
        population_id: state.selectedPopulationId,
      });
      console.error("Failed to create test:", error);
      // B10: builder copy is a catalog line or a typed transport line, never
      // the server's sentence.
      const body = (
        error as {
          response?: { data?: { code?: unknown; experimentId?: unknown } };
        }
      )?.response?.data;
      // The draft already launched a run: that run is where the user goes.
      if (
        body?.code === "questionnaire_draft_already_launched" &&
        typeof body.experimentId === "string"
      ) {
        router.push(`/tests/${body.experimentId}/results`);
        return;
      }
      // The draft moved past the reviewed revision (a save, or a launch that
      // was handed back): that pin can never launch again. Drop it and go
      // back to the builder, whose Review compiles the draft as it is now
      // and shows its price.
      if (body?.code === "questionnaire_launch_state_changed") {
        dispatch({
          type: "SET_SURVEY_JSON",
          payload: state.editedSurveyJson,
          revision: null,
          audience: null,
        });
        dispatch({ type: "SET_STEP", payload: 2 });
      }
      // Retry only when nobody can tell whether the launch landed; it sends
      // the same idempotency key, which the backend answers with that launch.
      const outcomeUnknown = transportFailureCopy(error)?.retryable === true;
      toast.error(
        requestFailureLine(
          error,
          i18n.t("creation.failedCreateStudy", { ns: "app" }),
        ),
        {
          duration: Infinity,
          ...(outcomeUnknown
            ? {
                action: {
                  label: i18n.t("audienceCreate.retry", { ns: "app" }),
                  onClick: () => handleSubmit(),
                },
              }
            : signInActionFor(error)),
        },
      );
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
      submittingRef.current = false;
    }
  };

  return {
    handleCompiledSurveyReady,
    handleSubmit,
    editStudyName,
    commitStudyName,
  };
}
